import { useGetProcedures } from '@vindr/services/actions/procedure.api';
import { INIT_PAGINATION, MODALITIES } from '@vindr/services/constants/common.constant';
import { PaginationProps } from '@vindr/services/interfaces';
import { Table } from '@vindr/ui';
import onPaginationChange from '@vindr/utils/state/onPaginationChange';
import { Button } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import Filter from './ProcedureFilter';
import useProcedureSearchColumns from './useProcedureSearchColumns';
import { addToArray } from '@vindr/utils/helpers/common.helper';
import onClickRow, { onDoubleClickRow } from '@vindr/utils/state/onClickRow';

type Procedure = ReturnType<typeof useGetProcedures>['data']['data'];

const ProcedureSearch = ({
  onSave,
  onCancel,
  institution_id,
  modality,
}: {
  onSave: (data: Procedure) => void;
  onCancel: () => void;
  institution_id: string;
  modality: MODALITIES;
}) => {
  const { t } = useTranslation('Vindoc');
  const columns = useProcedureSearchColumns();
  const initFilters = { institution_ids: [institution_id], modality };
  const [params, setParams] = useState(initFilters);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Procedure>([]);
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);
  const { data, isLoading, isFetching, refetch } = useGetProcedures({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    active: true,
    ...params,
    institution_ids: institution_id ? [institution_id] : null,
  });

  const count = data?.count;
  const procedureList = data?.data;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = useCallback(
    (params) => {
      setParams(params);
      // Set refetch to true only when force search
      setIsRefetch(true);
      refetch?.();
    },
    [refetch]
  );

  const handleSave = useCallback(
    (record?: Procedure[0]) => {
      const selected = addToArray(record || selectedRowKeys, []);

      return onSave(selected.map((it) => ({ ...it, timestamp: Date.now() })));
    },
    [onSave, selectedRowKeys]
  );

  return (
    <div className="flex flex-col">
      <Filter
        forceSearch={forceSearch}
        initFilters={initFilters}
        onChange={(p) => {
          setParams(p);
        }}
      />
      <Table
        columns={columns}
        count={count}
        dataSource={procedureList || []}
        loading={isLoading || isRefetch}
        pagination={count < pagination.size ? null : pagination}
        rowKey="id"
        scroll={{ y: 500 }}
        wrapperClassName="h-auto"
        rowSelection={{
          selectedRowKeys: addToArray(selectedRowKeys, []).map((it) => it.id),
          onChange: (_, selectedRows) => setSelectedRowKeys(selectedRows),
        }}
        onPaginationChange={(p) => {
          setPagination((draft) => onPaginationChange(draft, p));
        }}
        onRow={(record) => {
          return {
            onClick: (event) => onClickRow({ event, setSelectedRowKeys, record }),
            onDoubleClick: (event) =>
              onDoubleClickRow({
                event,
                callback: () => handleSave(record),
              }),
          };
        }}
      />
      <div className="footer mt-3 flex justify-end">
        <Button className="mr-1" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button type="primary" onClick={() => handleSave()}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default ProcedureSearch;
